import React, { useCallback, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  Flex,
} from "@chakra-ui/react";
import { Text14 } from "components/Text";

interface ConfirmationOptions {
  header: string;
  body: string | JSX.Element;
  subHeader?: string | JSX.Element;
  confirmButtonText?: string;
  cancelButtonText?: string;
  intent?: "warning" | "confirmation";
  onConfirm: () => void;
  onCancel?: () => void;
}

interface UseConfirmationReturn {
  confirm: (options: ConfirmationOptions) => void;
  ConfirmationDialog: React.FC;
}

export default function useConfirmation(): UseConfirmationReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback((opts: ConfirmationOptions) => {
    setOptions(opts);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    options?.onCancel?.();
    setOptions(null);
  }, [options]);

  const handleConfirm = useCallback(() => {
    options?.onConfirm();
    setIsOpen(false);
    setOptions(null);
  }, [options]);

  const intentStyling = options?.intent === "warning"
    ? { colorScheme: "red", variant: "outline" }
    : { colorScheme: "brand" };

  const ConfirmationDialog: React.FC = useCallback(() => {
    if (!options) return null;

    const { isValidElement } = React;

    return (
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={handleClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader px="9" pt="7" pb="2" fontSize="20px" fontWeight="700" color="cdsGrey.800">
              {options.header}
            </AlertDialogHeader>
            <AlertDialogBody px="9">
              <Flex direction="column" gap="3">
                {options.subHeader && (
                  isValidElement(options.subHeader) ? options.subHeader : (
                    <Text14 fontWeight="700">
                      {options.subHeader}
                    </Text14>
                  )
                )}
                {isValidElement(options.body) ? options.body : (
                  <Text14 whiteSpace="pre-wrap">
                    {options.body}
                  </Text14>
                )}
              </Flex>
            </AlertDialogBody>

            <AlertDialogFooter px="9">
              <ButtonGroup>
                <Button h="32px" ref={cancelRef} variant="outline" colorScheme="gray" onClick={handleClose} ml={3}>
                  {options.cancelButtonText || "Cancel"}
                </Button>
                <Button h="32px" colorScheme={intentStyling.colorScheme} onClick={handleConfirm}>
                  {options.confirmButtonText || "Confirm"}
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    );
  }, [isOpen, options, handleClose, handleConfirm, intentStyling.colorScheme]);

  return { confirm, ConfirmationDialog };
}
